import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ExternalLink, Loader2, Mic, NotebookPen, Search, UserX } from "lucide-react";
import { toast } from "sonner";
import { useVoiceSearch } from "@/hooks/useVoiceSearch";
import { PilatesEvolutionTimeline, type EvolutionRow } from "./PilatesEvolutionTimeline";

interface Patient {
  id: string;
  full_name: string;
  cpf: string | null;
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EVOLUTION_TRANSCRIBE_PROMPT =
  "Transcreva este áudio em português brasileiro. É uma nota de evolução de aula ou atendimento de Pilates. Retorne apenas a transcrição fiel do que foi falado, sem títulos nem comentários extras. Preserve parágrafos quando fizer sentido.";

function formatSupabaseCallError(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message.trim()) return e.message;
  if (e && typeof e === "object") {
    const o = e as { message?: unknown; details?: unknown };
    const msg = typeof o.message === "string" ? o.message.trim() : "";
    const det = typeof o.details === "string" ? o.details.trim() : "";
    const combined = [msg, det].filter(Boolean).join(" — ");
    if (combined) return combined;
  }
  return fallback;
}

export default function PilatesEvolutionExtension() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [sessionAtLocal, setSessionAtLocal] = useState(() => toDatetimeLocalValue(new Date()));
  const [notes, setNotes] = useState("");
  const [evolutions, setEvolutions] = useState<EvolutionRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingEvolutionId, setUpdatingEvolutionId] = useState<string | null>(null);
  const [deletingEvolutionId, setDeletingEvolutionId] = useState<string | null>(null);

  const onTranscriptionComplete = useCallback((text: string) => {
    setNotes((prev) => {
      const t = text.trim();
      if (!t) return prev;
      return prev.trim() ? `${prev.trim()}\n\n${t}` : t;
    });
  }, []);

  const transcribeOptions = useMemo(
    () => ({ prompt: EVOLUTION_TRANSCRIBE_PROMPT, maxOutputTokens: 2048 }),
    [],
  );

  const { isRecording, isTranscribing, startRecording, stopRecording } = useVoiceSearch({
    onTranscriptionComplete,
    maxDuration: 120_000,
    transcribeOptions,
    onError: (msg) => toast.error(msg),
  });

  const loadEvolutions = useCallback(async (patientId: string) => {
    setLoadingList(true);
    try {
      const { data } = await supabase
        .from("pilates_session_evolutions")
        .select("id, session_at, notes, author_name")
        .eq("patient_id", patientId)
        .order("session_at", { ascending: false })
        .throwOnError();

      setEvolutions((data as EvolutionRow[]) || []);
    } catch (e: unknown) {
      console.error(e);
      toast.error(formatSupabaseCallError(e, "Erro ao carregar evoluções"));
      setEvolutions([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      void loadEvolutions(selectedPatient.id);
    } else {
      setEvolutions([]);
    }
  }, [selectedPatient, loadEvolutions]);

  useEffect(() => {
    if (selectedPatient) {
      document.title = `elinia - ${selectedPatient.full_name}`;
    } else {
      document.title = "elinia - Saúde com Inteligência";
    }
    return () => {
      document.title = "elinia - Saúde com Inteligência";
    };
  }, [selectedPatient]);

  useEffect(() => {
    const pid = searchParams.get("patientId")?.trim();
    if (!pid || selectedPatient?.id === pid) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await supabase
          .from("patients_onboarding")
          .select("id, full_name, cpf")
          .eq("id", pid)
          .maybeSingle();
        if (cancelled) return;
        if (error || !data) return;
        setSelectedPatient({
          id: data.id,
          full_name: data.full_name,
          cpf: data.cpf,
        });
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, selectedPatient?.id]);

  const handleSearch = async () => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) {
      setSearchResults([]);
      return;
    }

    try {
      const cpfDigits = trimmedSearch.replace(/\D/g, "");
      const foundPatients = new Map<string, Patient>();

      const { data: patientsByName } = await supabase
        .from("patients_onboarding")
        .select("id, full_name, cpf")
        .ilike("full_name", `%${trimmedSearch}%`)
        .order("full_name", { ascending: true })
        .limit(30)
        .throwOnError();

      for (const patient of (patientsByName as Patient[]) || []) {
        foundPatients.set(patient.id, patient);
      }

      if (cpfDigits.length >= 3) {
        const { data: patientsByCpf } = await supabase
          .from("patients_onboarding")
          .select("id, full_name, cpf")
          .ilike("cpf", `%${cpfDigits}%`)
          .order("full_name", { ascending: true })
          .limit(30)
          .throwOnError();

        for (const patient of (patientsByCpf as Patient[]) || []) {
          foundPatients.set(patient.id, patient);
        }
      }

      setSearchResults(Array.from(foundPatients.values()));
    } catch (e: unknown) {
      console.error(e);
      toast.error(formatSupabaseCallError(e, "Erro ao buscar alunos"));
    }
  };

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchResults([]);
    setSearchTerm("");
    setSessionAtLocal(toDatetimeLocalValue(new Date()));
    setNotes("");
  };

  const handleSave = async () => {
    if (!selectedPatient) {
      toast.error("Selecione um aluno");
      return;
    }
    if (!notes.trim()) {
      toast.error("Digite a evolução ou use o microfone para transcrever");
      return;
    }

    const sessionAt = new Date(sessionAtLocal);
    if (Number.isNaN(sessionAt.getTime())) {
      toast.error("Data e hora inválidas");
      return;
    }

    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Sessão expirada");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();

      const authorName =
        profile?.full_name?.trim() || user.email?.split("@")[0]?.trim() || "Profissional";

      await supabase
        .from("pilates_session_evolutions")
        .insert({
          professional_id: user.id,
          patient_id: selectedPatient.id,
          session_at: sessionAt.toISOString(),
          notes: notes.trim(),
          author_name: authorName,
        })
        .throwOnError();

      toast.success("Evolução registrada");
      setNotes("");
      setSessionAtLocal(toDatetimeLocalValue(new Date()));
      await loadEvolutions(selectedPatient.id);
    } catch (e: unknown) {
      toast.error(formatSupabaseCallError(e, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEvolution = async (
    evolutionId: string,
    sessionAtIso: string,
    editedNotes: string,
  ): Promise<boolean> => {
    if (!selectedPatient) {
      toast.error("Selecione um aluno");
      return false;
    }

    const trimmedNotes = editedNotes.trim();
    if (!trimmedNotes) {
      toast.error("A evolução não pode ficar vazia");
      return false;
    }

    const parsedSessionAt = new Date(sessionAtIso);
    if (Number.isNaN(parsedSessionAt.getTime())) {
      toast.error("Data e hora inválidas");
      return false;
    }

    setUpdatingEvolutionId(evolutionId);
    try {
      await supabase
        .from("pilates_session_evolutions")
        .update({
          session_at: parsedSessionAt.toISOString(),
          notes: trimmedNotes,
        })
        .eq("id", evolutionId)
        .eq("patient_id", selectedPatient.id)
        .throwOnError();

      toast.success("Evolução atualizada");
      await loadEvolutions(selectedPatient.id);
      return true;
    } catch (e: unknown) {
      toast.error(formatSupabaseCallError(e, "Erro ao atualizar evolução"));
      return false;
    } finally {
      setUpdatingEvolutionId(null);
    }
  };

  const handleDeleteEvolution = async (evolutionId: string): Promise<boolean> => {
    if (!selectedPatient) {
      toast.error("Selecione um aluno");
      return false;
    }

    setDeletingEvolutionId(evolutionId);
    try {
      await supabase
        .from("pilates_session_evolutions")
        .delete()
        .eq("id", evolutionId)
        .eq("patient_id", selectedPatient.id)
        .throwOnError();

      toast.success("Evolução excluída");
      await loadEvolutions(selectedPatient.id);
      return true;
    } catch (e: unknown) {
      toast.error(formatSupabaseCallError(e, "Erro ao excluir evolução"));
      return false;
    } finally {
      setDeletingEvolutionId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/aplicativos")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-sky-100 to-blue-100 p-2 dark:from-sky-900/40 dark:to-blue-900/40">
              <NotebookPen className="h-6 w-6 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Evolução Alunos Pilates</h1>
              <p className="text-muted-foreground">Registre cada atendimento e acompanhe a linha do tempo</p>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      <Card className="border-sky-100 dark:border-sky-900/50">
        <CardHeader>
          <CardTitle className="text-lg">Aluno</CardTitle>
          <CardDescription>Busque pelo nome ou CPF e selecione o aluno para ver e registrar evoluções.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!selectedPatient ? (
            <>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  placeholder="Buscar aluno por nome ou CPF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && void handleSearch()}
                />
                <Button type="button" onClick={() => void handleSearch()} className="shrink-0">
                  <Search className="mr-2 h-4 w-4" />
                  Buscar
                </Button>
              </div>
              {searchResults.length > 0 && (
                <ul className="max-h-64 space-y-2 overflow-y-auto rounded-lg border bg-muted/30 p-2">
                  {searchResults.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                        onClick={() => handleSelectPatient(p)}
                      >
                        <span className="font-medium">{p.full_name}</span>
                        {p.cpf ? (
                          <span className="mt-0.5 block text-xs text-muted-foreground">CPF: {p.cpf}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Aluno selecionado</p>
                <p className="text-lg font-semibold">{selectedPatient.full_name}</p>
                {selectedPatient.cpf ? (
                  <p className="text-sm text-muted-foreground">CPF: {selectedPatient.cpf}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedPatient(null)}
                >
                  <UserX className="mr-2 h-4 w-4" />
                  Trocar aluno
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={`/app/pacientes/${selectedPatient.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Ver perfil
                  </a>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedPatient ? (
        <>
          <Card className="border-sky-100 dark:border-sky-900/50">
            <CardHeader>
              <CardTitle className="text-lg">Nova evolução</CardTitle>
              <CardDescription>Data e hora do atendimento, texto livre ou transcrição por voz.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="session-at">Data e hora do atendimento</Label>
                <Input
                  id="session-at"
                  type="datetime-local"
                  value={sessionAtLocal}
                  onChange={(e) => setSessionAtLocal(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="evolution-notes">Evolução</Label>
                  <Button
                    type="button"
                    variant={isRecording ? "destructive" : "outline"}
                    size="sm"
                    disabled={isTranscribing}
                    onClick={() => {
                      if (isRecording) stopRecording();
                      else void startRecording();
                    }}
                  >
                    {isTranscribing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Transcrevendo…
                      </>
                    ) : isRecording ? (
                      "Parar e transcrever"
                    ) : (
                      <>
                        <Mic className="mr-2 h-4 w-4" />
                        Gravar áudio
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="evolution-notes"
                  placeholder="Descreva o que foi trabalhado, resposta do aluno, progressão…"
                  className="min-h-[140px] resize-y"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isTranscribing}
                />
              </div>
              <Button
                type="button"
                className="bg-sky-600 hover:bg-sky-700 dark:bg-sky-600 dark:hover:bg-sky-500"
                disabled={saving || isTranscribing || isRecording}
                onClick={() => void handleSave()}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando…
                  </>
                ) : (
                  "Salvar evolução"
                )}
              </Button>
            </CardContent>
          </Card>

          {loadingList ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <PilatesEvolutionTimeline
              items={evolutions}
              onEdit={handleUpdateEvolution}
              updatingId={updatingEvolutionId}
              onDelete={handleDeleteEvolution}
              deletingId={deletingEvolutionId}
            />
          )}
        </>
      ) : null}
    </div>
  );
}
